import { formatISODate } from '@common/utils/dateUtils';
import { ActionButton } from '@common/components';
import {
  EditIcon,
  ModifyReportCriteriaIcon,
  EnableIcon,
  DisableIcon,
  Delete2Icon,
} from '@common/components/icons';
import { ActionsCell } from '@common/components/Table/Cells';
import EditableStatusDropdown from '@common/components/EditableStatusDropdown/EditableStatusDropdown';
import * as styles from './styles.module.css';

/**
 * Check if the current user can modify the saved report
 * Admin can modify any report, non-admin can only modify their own
 * @param {Object} savedReport - The saved report object
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @param {number} userContactId - The current user's contact ID
 * @returns {boolean} - Whether the user can modify this report
 */
const canModifyReport = (savedReport, isAdmin, userContactId) => {
  if (isAdmin) return true;
  return savedReport.createdBy === userContactId;
};

/**
 * Create table columns for saved reports
 * @param {function} t - Translation function
 * @param {Object} options - Column options with action handlers and user context
 * @param {function} options.onEditNameDescription - Handler for edit name/description action
 * @param {function} options.onModifyReportCriteria - Handler for modify report criteria action
 * @param {function} options.onEnable - Handler for enable action
 * @param {function} options.onDisable - Handler for disable action
 * @param {function} options.onDelete - Handler for delete action
 * @param {function} options.onUpdateStatus - Handler for inline status update
 * @param {boolean} options.isAdmin - Whether the current user is an admin
 * @param {number} options.userContactId - The current user's contact ID
 */
export const createColumns = (t, { onEditNameDescription, onModifyReportCriteria, onEnable, onDisable, onDelete, onUpdateStatus, isAdmin, userContactId }) => {
  const actionsMenu = [
    {
      key: 'editNameDescription',
      component: (
        <ActionButton
          icon={EditIcon}
          text={t('savedReports.actions.editNameDescription')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onEditNameDescription?.(row.original);
      },
      hidden: (row) => !canModifyReport(row.original, isAdmin, userContactId),
    },
    {
      key: 'modifyReportCriteria',
      component: (
        <ActionButton
          icon={ModifyReportCriteriaIcon}
          text={t('savedReports.actions.modifyReportCriteria')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onModifyReportCriteria?.(row.original);
      },
      hidden: (row) => !canModifyReport(row.original, isAdmin, userContactId),
    },
    {
      key: 'enable',
      component: (
        <ActionButton
          icon={EnableIcon}
          text={t('common.enable')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onEnable?.(row.original);
      },
      // Hide if already enabled OR if user doesn't have permission
      hidden: (row) => row.original.status === 1 || !canModifyReport(row.original, isAdmin, userContactId),
    },
    {
      key: 'disable',
      component: (
        <ActionButton
          icon={DisableIcon}
          text={t('common.disable')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onDisable?.(row.original);
      },
      // Hide if already disabled OR if user doesn't have permission
      hidden: (row) => row.original.status === 0 || !canModifyReport(row.original, isAdmin, userContactId),
    },
    {
      key: 'delete',
      component: (
        <ActionButton
          icon={Delete2Icon}
          text={t('savedReports.actions.delete')}
          className={`${styles['action-button']} ${styles['delete-action']}`}
        />
      ),
      onClick: (row) => {
        onDelete?.(row.original);
      },
      hidden: (row) => !canModifyReport(row.original, isAdmin, userContactId),
    },
  ];

  return [
    {
      id: 'select',
      staticWidth: 37,
      enableResizing: false,
      enableSorting: false,
      meta: { label: t('common.selection') },
    },
    {
      accessorKey: 'name',
      header: t('common.name'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 200,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 250,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'dashboardName',
      header: t('savedReports.columns.reportTemplate'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 180,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'status',
      header: t('savedReports.columns.status'),
      enableColumnFilter: true,
      enableColumnResizing: false,
      staticWidth: 130,
      cell: ({ getValue, row }) => {
        const savedReport = row.original;
        const canModify = canModifyReport(savedReport, isAdmin, userContactId);
        return (
          <EditableStatusDropdown
            value={getValue()}
            onUpdate={(newValue) => onUpdateStatus?.(savedReport, newValue)}
            showStatusDot
            disabled={!canModify}
          />
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('savedReports.columns.createdAt'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 150,
      cell: ({ getValue }) => formatISODate(getValue()),
    },
    // Only include actions column if user has admin privileges
    // Non-admin users only see their own reports (backend filter) and can modify them inline via status dropdown
    ...(isAdmin ? [{
      id: 'actions',
      staticWidth: 27,
      cell: ({ row }) => <ActionsCell row={row} actionsMenu={actionsMenu} />,
      enableResizing: false,
      enableSorting: false,
      meta: { label: t('common.actions') },
    }] : []),
  ];
};
